import { TextArea } from 'jl-design-system/components/text-area/TextArea';
import DateInput from '@JohnLewisPartnership/jl-design-system/dist/components/date-input/DateInput';

import { Checkbox, Select, TextField, RadioQuestion } from '../../form';

import { CheckboxesQuestion } from '../question/checkboxes-question';
import { ChoiceQuestion } from '../question/choice-question';
import { HeightData } from '../question/height-data';

import stylesQuestion from '../question/Question.scss';

import {
  restoreHeightAnswer,
  restoreAnswer,
  restoreCheckboxAnswer,
  restoreDropdownCheckboxAnswer,
  restoreDropdownWithPriceAnswer,
  restoreMultiSelectAnswer,
} from './restore-answers';

import {
  isNotEmptyAnswer,
  isCheckboxAnswerNotEmpty,
  formatHeightAnswer,
  formatAnswer,
  formatDropdownCheckboxAnswer,
  formatDateAnswer,
  formatBooleanAnswer,
  formatPrice,
  formatArrayAnswer,
} from './format-answers';

import { feet, inches, cm } from './normalize';

export const JRNI_QUESTION_TYPE = {
  SELECT: 'selectable',
  SELECT_MULTI: 'selectable-multi',
  IMAGE_SELECT: 'image-select',
  IMAGE_SELECT_VERTICAL: 'image-select-vertical',
  IMAGE_SELECT_SMALL: 'image-select-small',
  HEIGHT: 'height',
  CHECKBOX: 'check',
  CHECKBOX_PRICE: 'check-price',
  CHECKBOXES: 'checkboxes',
  DATE_INPUT: 'date',
  TEXTAREA: 'text_area',
  TEXT_FIELD: 'text_field',
  DROPDOWN: 'select',
  DROPDOWN_PRICE: 'select-price',
  RADIO: 'radio',
  RADIO_PRICE: 'radio-price',
  DROPDOWN_CHECKBOX: 'dropdown-checkbox',
};
export const OCAB_QUESTION_TYPE = {
  SELECT: 'SELECT',
  SELECT_MULTI: 'SELECT_MULTI',
  IMAGE_SELECT: 'IMAGE_SELECT',
  IMAGE_SELECT_VERTICAL: 'IMAGE_SELECT_VERTICAL',
  IMAGE_SELECT_SMALL: 'IMAGE_SELECT_SMALL',
  HEIGHT: 'HEIGHT',
  CHECKBOX: 'checkbox',
  CHECKBOX_PRICE: 'CHECKBOX_PRICE',
  CHECKBOXES: 'CHECKBOXES',
  DATE_INPUT: 'DATE_INPUT',
  TEXTAREA: 'TEXTAREA',
  TEXT_FIELD: 'TEXT_FIELD',
  DROPDOWN: 'DROPDOWN',
  DROPDOWN_PRICE: 'DROPDOWN_PRICE',
  RADIO: 'RADIO',
  RADIO_PRICE: 'RADIO_PRICE',
  DROPDOWN_CHECKBOX: 'DROPDOWN_CHECKBOX',
};

export const SelectType = {
  MULTI: 'multi',
  SINGLE: 'single',
};

const questionTypeTemplates = {
  [JRNI_QUESTION_TYPE.SELECT]: {
    type: OCAB_QUESTION_TYPE.SELECT,
    selectType: SelectType.SINGLE,
    component: ChoiceQuestion,
    optionClassName: stylesQuestion.OptionsList_4_col,
    isNotEmptyAnswer,
    formatAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.SELECT_MULTI]: {
    type: OCAB_QUESTION_TYPE.SELECT,
    selectType: SelectType.MULTI,
    component: ChoiceQuestion,
    optionClassName: stylesQuestion.OptionsList_6_col,
    isNotEmptyAnswer,
    formatAnswer: formatArrayAnswer,
    restoreAnswer: restoreMultiSelectAnswer,
  },
  [JRNI_QUESTION_TYPE.HEIGHT]: {
    type: OCAB_QUESTION_TYPE.HEIGHT,
    component: HeightData,
    isNotEmptyAnswer,
    restoreAnswer: restoreHeightAnswer,
    formatAnswer: formatHeightAnswer,
    fields: [
      {
        name: 'feet',
        id: 'feet',
        normalizeField: feet,
      },
      {
        name: 'inches',
        id: 'inches',
        normalizeField: inches,
      },
    ],
    fieldsCm: [
      {
        name: 'cm',
        id: 'cm',
        normalizeField: cm,
      },
    ],
  },
  [JRNI_QUESTION_TYPE.IMAGE_SELECT]: {
    type: OCAB_QUESTION_TYPE.IMAGE_SELECT,
    selectType: SelectType.SINGLE,
    component: ChoiceQuestion,
    questionTypeClassName: stylesQuestion.ImageSelect,
    optionClassName: stylesQuestion.OptionsList_1_col,
    isNotEmptyAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.IMAGE_SELECT_VERTICAL]: {
    type: OCAB_QUESTION_TYPE.IMAGE_SELECT_VERTICAL,
    selectType: SelectType.SINGLE,
    component: ChoiceQuestion,
    questionTypeClassName: stylesQuestion.ImageSelect,
    questionClassName: stylesQuestion.ImageSelect_vertical,
    optionClassName: stylesQuestion.OptionsList_2_col,
    isNotEmptyAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.IMAGE_SELECT_SMALL]: {
    type: OCAB_QUESTION_TYPE.IMAGE_SELECT_SMALL,
    selectType: SelectType.SINGLE,
    component: ChoiceQuestion,
    questionTypeClassName: stylesQuestion.ImageSelect,
    questionClassName: stylesQuestion.ImageSelect_small,
    optionClassName: stylesQuestion.OptionsList_3_col,
    isNotEmptyAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.TEXTAREA]: {
    type: OCAB_QUESTION_TYPE.TEXTAREA,
    component: TextArea,
    questionClassName: stylesQuestion.TextareaQuestion,
    formatAnswer,
    isNotEmptyAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.CHECKBOX]: {
    type: OCAB_QUESTION_TYPE.CHECKBOX,
    component: Checkbox,
    questionClassName: stylesQuestion.CheckboxQuestion,
    isNotEmptyAnswer: isCheckboxAnswerNotEmpty,
    formatAnswer: formatBooleanAnswer,
    restoreAnswer: restoreCheckboxAnswer,
  },
  [JRNI_QUESTION_TYPE.CHECKBOX_PRICE]: {
    type: OCAB_QUESTION_TYPE.CHECKBOX,
    component: Checkbox,
    questionClassName: stylesQuestion.CheckboxQuestion,
    isNotEmptyAnswer: isCheckboxAnswerNotEmpty,
    formatAnswer: formatPrice,
    restoreAnswer: restoreCheckboxAnswer,
  },
  [JRNI_QUESTION_TYPE.CHECKBOXES]: {
    type: OCAB_QUESTION_TYPE.CHECKBOXES,
    selectType: SelectType.MULTI,
    component: CheckboxesQuestion,
    isNotEmptyAnswer,
    formatAnswer: formatArrayAnswer,
    restoreAnswer: restoreMultiSelectAnswer,
  },
  [JRNI_QUESTION_TYPE.DROPDOWN_CHECKBOX]: {
    type: OCAB_QUESTION_TYPE.DROPDOWN_CHECKBOX,
    component: Checkbox,
    questionClassName: stylesQuestion.CheckboxQuestion,
    isNotEmptyAnswer: isCheckboxAnswerNotEmpty,
    formatAnswer: formatDropdownCheckboxAnswer,
    restoreAnswer: restoreDropdownCheckboxAnswer,
  },
  [JRNI_QUESTION_TYPE.TEXT_FIELD]: {
    type: OCAB_QUESTION_TYPE.TEXT_FIELD,
    component: TextField,
    questionClassName: stylesQuestion.TextinputQuestion,
    formatAnswer,
    isNotEmptyAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.DROPDOWN]: {
    type: OCAB_QUESTION_TYPE.DROPDOWN,
    component: Select,
    questionClassName: stylesQuestion.Select,
    formatAnswer,
    isNotEmptyAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.DROPDOWN_PRICE]: {
    type: OCAB_QUESTION_TYPE.DROPDOWN_PRICE,
    component: Select,
    questionClassName: stylesQuestion.Select,
    formatAnswer,
    isNotEmptyAnswer,
    restoreAnswer: restoreDropdownWithPriceAnswer,
  },
  [JRNI_QUESTION_TYPE.RADIO]: {
    type: OCAB_QUESTION_TYPE.RADIO,
    component: RadioQuestion,
    formatAnswer,
    isNotEmptyAnswer,
    restoreAnswer,
  },
  [JRNI_QUESTION_TYPE.RADIO_PRICE]: {
    type: OCAB_QUESTION_TYPE.RADIO_PRICE,
    component: Select,
    questionClassName: stylesQuestion.Select,
    formatAnswer,
    isNotEmptyAnswer,
    restoreAnswer: restoreDropdownWithPriceAnswer,
  },
  [JRNI_QUESTION_TYPE.DATE_INPUT]: {
    type: OCAB_QUESTION_TYPE.DATE_INPUT,
    component: DateInput,
    questionClassName: stylesQuestion.DateInput,
    formatAnswer: formatDateAnswer,
    isNotEmptyAnswer,
    restoreAnswer,
  },
};

export const buildRawQuestion = (questionType, treatDropdownQuestionAsChoice) => {
  if (treatDropdownQuestionAsChoice && questionType === JRNI_QUESTION_TYPE.DROPDOWN) {
    return {
      ...questionTypeTemplates[JRNI_QUESTION_TYPE.SELECT],
      optionClassName: stylesQuestion.OptionsList_4_col,
    };
  }
  return questionTypeTemplates[questionType];
};
